import {
	Box,
	Divider,
	FormControlLabel,
	FormHelperText,
	Grid,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import {
	API_DATE_FORMAT,
	DISPLAY_DATE_FORMAT,
	notFutureDate,
} from '@/utils/dateHelper';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { formatAge } from '@/utils/ageHelper';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';

interface Props {
	type: FormType;
	details?: any;
	allEnumList?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditWellBeingScaleForm = forwardRef(
	({ type, setType, details, allEnumList }: Props, ref) => {
		const { t } = useTranslation();

		const [answerList, setAnswerList] = useState(() =>
			Array.from({ length: 20 }, (_, i) => {
				const id = i + 1;
				return {
					id,
					title: t(`preAdmission.title${id}`) + '*',
					answer1: false,
					answer2: false,
					answer3: false,
					answer4: false,
					answer5: false,
				};
			})
		);

		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) {
				methods.reset(details);
				if (details.answerList) {
					const restoredList = details.answerList?.map(
						(score: number, index: number) => {
							const id = index + 1;
							const title = t(`preAdmission.title${id}`) + '*';
							const row: any = {
								id,
								title,
								answer1: false,
								answer2: false,
								answer3: false,
								answer4: false,
								answer5: false,
							};

							row[`answer${score}`] = true;
							return row;
						}
					);
					setAnswerList(restoredList);
					methods.setValue('answerList', restoredList);
					console.log(restoredList);
				}
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			fillingDate: Yup.string()
				.required(t('errorMessage.pleaseSelect'))
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			answerList: Yup.array()
				.of(
					Yup.object().test(
						'at-least-one-answer',
						t('errorMessage.pleaseSelect'), // 错误提示
						(row) => {
							if (!row) return false;
							const { answer1, answer2, answer3, answer4, answer5 } =
								row as any;
							return [answer1, answer2, answer3, answer4, answer5].some(
								(val) => val === true
							);
						}
					)
				)
				.required(t('errorMessage.pleaseSelect'))
				.test(
					'all-rows-answered',
					t('errorMessage.pleaseAnswerAllQuestions'),
					(list) => {
						if (!Array.isArray(list)) return false;
						return list.length === 20; // 可选：确保数量正确
					}
				),

			diagnosisSpecify: Yup.string().when('diagnosis', {
				is: (diagnosis: string) => diagnosis && diagnosisSpecifyIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			vocationalTrainingSpecify: Yup.string().when('vocationalTraining', {
				is: (vocationalTraining: string) =>
					vocationalTraining && vocationalTrainingSpecifyIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const answerListField = useController({
			name: 'answerList',
			control: methods.control,
		});

		const watchDiagnosis = methods.watch('diagnosis');
		const watchVocationalTraining = methods.watch('vocationalTraining');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.setValue('answerList', answerList);
				console.log(JSON.stringify(methods.getValues('answerList')));
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const diagnosisSpecifyIsOther = () => {
			const obj = allEnumList?.data.DiagnosisEnum.find(
				(item: any) => item.desc == '其他診斷'
			);
			if (obj && watchDiagnosis == obj.code) return true;
			return false;
		};
		const vocationalTrainingSpecifyIsOther = () => {
			const obj = allEnumList?.data.VocationalTrainingEnum.find(
				(item: any) => item.desc == '其他'
			);
			if (obj && watchVocationalTraining == obj.code) return true;
			return false;
		};

		const radioRenderCell = (params: any, score: number) => {
			const { id, row, api } = params;

			const isChecked = row[`answer${score}`] === true;

			const handleChange = () => {
				const updates: Record<string, boolean> = {
					answer1: false,
					answer2: false,
					answer3: false,
					answer4: false,
					answer5: false,
				};
				updates[`answer${score}`] = true;

				api.updateRows([{ id, ...updates }]);
				const list = api.getAllRowIds().map((id: any) => api.getRow(id));
				setAnswerList(list);

				methods.setValue('answerList', list);
				methods.trigger();
			};
			return (
				<Radio checked={isChecked} value={score} onChange={handleChange} />
			);
		};

		const columns: GridColDef[] = [
			{
				field: 'title',
				headerName: '',
				flex: 30,
			},
			{
				field: 'answer1',
				headerName: t('preAdmission.score1'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 1),
			},
			{
				field: 'answer2',
				headerName: t('preAdmission.score2'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 2),
			},
			{
				field: 'answer3',
				headerName: t('preAdmission.score3'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 3),
			},
			{
				field: 'answer4',
				headerName: t('preAdmission.score4'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 4),
			},
			{
				field: 'answer5',
				headerName: t('preAdmission.score5'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 5),
			},
		];
		return (
			<>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppDatePicker
									control={methods.control}
									name='fillingDate'
									label={t('preAdmission.fillingDate') + '*'}
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.userName')}
									value={details?.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.idNumber')}
									value={details?.hkid}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_fileNo')}
									value={details?.fileNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_unit')}
									value={details?.branchName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.birthday')}
									value={
										details?.dateOfBirth
											? dayjs(details?.dateOfBirth)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_age')}
									value={formatAge(
										details?.currentAge,
										details?.currentAgeEstimated,
										'zh'
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_sex')}
									value={details?.sexDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.serviceUsageTime')}
									value={details?.serviceDate}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='diagnosis'
									label={t('preAdmission.zh_diagnose')}
									options={(allEnumList?.data.DiagnosisEnum || []).map(
										(item: any) => {
											return {
												...item,
												id: item.code,
												name: item.desc,
											};
										}
									)}
									handleChange={() => {
										methods.setValue('diagnosisSpecify', '');
										methods.clearErrors('diagnosisSpecify');
									}}
								/>
							</Grid>

							<Grid size={6}>
								<AppTextField
									disabled={!diagnosisSpecifyIsOther()}
									control={methods.control}
									name='diagnosisSpecify'
									label={`${t('preAdmission.zh_diagnose_other')}${
										diagnosisSpecifyIsOther() ? '*' : ''
									}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									control={methods.control}
									name='vocationalTraining'
									label={t('preAdmission.zh_training')}
									options={(allEnumList?.data.VocationalTrainingEnum || []).map(
										(item: any) => {
											return {
												...item,
												id: item.code,
												name: item.desc,
											};
										}
									)}
									handleChange={() => {
										methods.setValue('vocationalTrainingSpecify', '');
										methods.clearErrors('vocationalTrainingSpecify');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!vocationalTrainingSpecifyIsOther()}
									control={methods.control}
									name='vocationalTrainingSpecify'
									label={`${t('preAdmission.zh_training_other')}${
										vocationalTrainingSpecifyIsOther() ? '*' : ''
									}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='education'
									label={t('preAdmission.degreeEducation')}
									options={(allEnumList?.data.EducationEnum || []).map(
										(item: any) => {
											return {
												...item,
												id: item.code,
												name: item.desc,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='degreeOfFamilySupport'
									label={t('preAdmission.degreeOfFamilySupport')}
									options={(
										allEnumList?.data.DegreeOfFamilySupportEnum || []
									).map((item: any) => {
										return {
											...item,
											id: item.code,
											name: item.desc,
										};
									})}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />

						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.questionnaire')}
						</Typography>
						<Typography
							variant='body2'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.wbsTips')}
						</Typography>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12} display='flex'>
								<LocalDataGrid
									rows={answerList}
									columns={columns}
									getRowId={(row) => row.id}
									pagination={false}
								/>
							</Grid>
						</Grid>
					</form>
					<FormHelperText color='error' style={{ color: '#d32f2f' }}>
						{answerListField.fieldState.error
							? t('errorMessage.pleaseSelect')
							: ''}
					</FormHelperText>
				</FormProvider>
			</>
		);
	}
);

EditWellBeingScaleForm.displayName = 'EditWellBeingScaleForm';

export default EditWellBeingScaleForm;
